import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MapPin, Plus, Edit2, Trash2, ArrowLeft, CreditCard, CheckCircle } from 'lucide-react'
import { useCart } from '../contexts/CartContext'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import { Address } from '../types'
import ProductImage from '../components/ProductImage'
import { getCache, getCacheStale, setCache } from '../lib/cache'

const Checkout = () => {
  const { storeId } = useParams<{ storeId: string }>()
  const navigate = useNavigate()
  const { getStoreCart, clearStoreCart } = useCart()
  const { user, isAuthenticated } = useAuth()
  
  const storeCart = storeId ? getStoreCart(storeId) : null
  const items = storeCart?.items || []
  const currentStore = storeCart?.store || null

  // All hooks must be declared before any conditional returns
  const [addresses, setAddresses] = useState<Address[]>([])
  const [selectedAddressId, setSelectedAddressId] = useState<string>('')
  const [showAddressForm, setShowAddressForm] = useState(false)
  const [editingAddress, setEditingAddress] = useState<Address | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<string>('cash')
  const [notes, setNotes] = useState('')

  // Ref para prevenir ejecuciones duplicadas (React StrictMode)
  const loadingAddressesRef = useRef(false)

  // Address form state
  const [addressForm, setAddressForm] = useState({
    name: '',
    phone: user?.phone || '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    instructions: '',
    isDefault: false
  })

  const deliveryFee = currentStore?.deliveryFee || 0
  const subtotal = items.reduce((sum, item) => sum + (Number(item.total) || 0), 0)
  // Calcular ITBIS solo si el negocio cobra ITBIS
  const chargesItbis = currentStore?.chargesItbis !== false // Por defecto true si no está definido
  const taxAmount = chargesItbis ? Math.round(subtotal * 0.18) : 0
  const finalTotal = subtotal + deliveryFee + taxAmount

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para realizar una compra')
      navigate('/login', { state: { from: `/checkout/${storeId}` } })
    }
  }, [isAuthenticated, navigate, storeId])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    // Si el carrito está vacío, no hacer validaciones adicionales ni mostrar errores
    // Esto evita errores cuando el carrito se limpia después de completar un pedido
    if (!storeCart || items.length === 0) {
      return
    }

    // Solo validar correspondencia si hay items en el carrito y hay un storeId
    if (storeId && currentStore?.id && currentStore.id !== storeId) {
      toast.error('El carrito no corresponde a este negocio')
      navigate('/cart')
      return
    }

    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingAddressesRef.current) return
    loadingAddressesRef.current = true

    loadAddresses().finally(() => {
      loadingAddressesRef.current = false
    })
  }, [storeId, isAuthenticated, currentStore, items.length, navigate, storeCart])

  const loadAddresses = async () => {
    if (!user?.id) return
    
    const cacheKey = `checkout_addresses_${user.id}`
    
    // Intentar cargar desde cache primero
    const cachedAddresses = getCacheStale<Address[]>(cacheKey)
    if (cachedAddresses && cachedAddresses.length > 0) {
      setAddresses(cachedAddresses)
      const defaultAddress = cachedAddresses.find((a: Address) => a.isDefault)
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id)
      } else if (cachedAddresses.length > 0) {
        setSelectedAddressId(cachedAddresses[0].id)
      }
      setLoading(false)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }

    try {
      setLoading(true)
      const response = await api.getAddresses()
      if (response.success) {
        const addressesData = response.data || []
        setAddresses(addressesData)
        const defaultAddress = addressesData.find((a: Address) => a.isDefault)
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id)
        } else if (addressesData.length > 0) {
          setSelectedAddressId(addressesData[0].id)
        }
        
        // Guardar en cache (10 minutos de TTL - direcciones cambian poco)
        setCache(cacheKey, addressesData, 10 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading addresses:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedAddresses) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSaveAddress = async () => {
    if (!addressForm.street || !addressForm.city || !addressForm.state) {
      toast.error('Completa todos los campos requeridos')
      return
    }

    try {
      if (editingAddress) {
        await api.updateAddress(editingAddress.id, addressForm)
        toast.success('Dirección actualizada')
      } else {
        const response = await api.createAddress(addressForm)
        if (response.success) {
          toast.success('Dirección creada')
          if (response.data?.id) {
            setSelectedAddressId(response.data.id)
          }
        }
      }
      setShowAddressForm(false)
      setEditingAddress(null)
      setAddressForm({
        name: '',
        phone: user?.phone || '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        instructions: '',
        isDefault: false
      })
      loadAddresses()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar dirección')
    }
  }

  const handleDeleteAddress = async (addressId: string) => {
    if (!confirm('¿Estás seguro de eliminar esta dirección?')) return

    try {
      await api.deleteAddress(addressId)
      toast.success('Dirección eliminada')
      if (selectedAddressId === addressId) {
        setSelectedAddressId('')
      }
      loadAddresses()
    } catch (error) {
      toast.error('Error al eliminar dirección')
    }
  }

  const handleEditAddress = (address: Address) => {
    setEditingAddress(address)
    setAddressForm({
      name: address.street || '',
      phone: user?.phone || '',
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zipCode || '',
      instructions: address.instructions || '',
      isDefault: Boolean(address.isDefault)
    })
    setShowAddressForm(true)
  }

  const handleSubmitOrder = async () => {
    if (!selectedAddressId) {
      if (addresses.length === 0) {
        toast.error('Debes agregar una dirección de entrega para completar tu pedido', {
          duration: 5000,
          icon: '📍',
        })
        // Mostrar automáticamente el formulario de dirección
        setEditingAddress(null)
        setAddressForm({
          name: '',
          phone: user?.phone || '',
          street: '',
          city: '',
          state: '',
          zipCode: '',
          instructions: '',
          isDefault: false
        })
        setShowAddressForm(true)
      } else {
        toast.error('Por favor selecciona una dirección de entrega para continuar', {
          duration: 4000,
        })
      }
      return
    }

    if (!storeId) {
      toast.error('Negocio no válido')
      return
    }

    setSubmitting(true)
    try {
      const orderData = {
        storeId,
        items: items.map(item => ({
          productId: item.product.id,
          variantId: item.variantId,
          quantity: item.quantity,
          notes: item.notes
        })),
        addressId: selectedAddressId,
        notes,
        paymentMethod
      }

      const response = await api.createOrder(orderData)
      if (response.success) {
        toast.success('Pedido creado exitosamente')
        // Navegar primero, luego limpiar el carrito para evitar que el useEffect se ejecute
        navigate(`/orders`)
        // Limpiar el carrito después de navegar para evitar efectos secundarios
        setTimeout(() => {
        if (storeId) {
          clearStoreCart(storeId)
        }
        }, 100)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al crear el pedido')
    } finally {
      setSubmitting(false)
    }
  }

  // Conditional returns after all hooks
  if (!isAuthenticated) {
    return null
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center pb-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Cargando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-20 sm:pb-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header - Mobile First */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-4 sm:mb-6 sticky top-0 z-10">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/cart')}
              className="p-2 sm:p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl sm:rounded-2xl transition-all duration-200 active:scale-95"
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Finalizar Compra</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">{currentStore?.name}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Address Selection */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-6 lg:p-7">
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl sm:rounded-2xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                  </div>
                  Dirección de Entrega
                </h2>
                <button
                  onClick={() => {
                    setEditingAddress(null)
                    setAddressForm({
                      name: '',
                      phone: user?.phone || '',
                      street: '',
                      city: '',
                      state: '',
                      zipCode: '',
                      instructions: '',
                      isDefault: false
                    })
                    setShowAddressForm(true)
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 sm:px-5 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl sm:rounded-2xl hover:from-primary-700 hover:to-primary-800 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Nueva Dirección</span>
                  <span className="sm:hidden">Nueva</span>
                </button>
              </div>

              {showAddressForm ? (
                <div className="space-y-4 sm:space-y-5 p-4 sm:p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
                        Nombre / Etiqueta
                      </label>
                      <input
                        type="text"
                        value={addressForm.name}
                        onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })}
                        placeholder="Casa, Trabajo, etc."
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        value={addressForm.phone}
                        onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
                      Calle y Número *
                    </label>
                    <input
                      type="text"
                      value={addressForm.street}
                      onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
                        Ciudad *
                      </label>
                      <input
                        type="text"
                        value={addressForm.city}
                        onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
                        Provincia *
                      </label>
                      <input
                        type="text"
                        value={addressForm.state}
                        onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
                        Código Postal
                      </label>
                      <input
                        type="text"
                        value={addressForm.zipCode}
                        onChange={(e) => setAddressForm({ ...addressForm, zipCode: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
                      Instrucciones de Entrega
                    </label>
                    <textarea
                      value={addressForm.instructions}
                      onChange={(e) => setAddressForm({ ...addressForm, instructions: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white resize-none"
                      placeholder="Ej: Llamar antes de llegar, Dejar en la puerta, etc."
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(addressForm.isDefault)}
                        onChange={(e) => setAddressForm({ ...addressForm, isDefault: e.target.checked })}
                        className="w-5 h-5 rounded border-2 border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                      />
                      <span className="text-sm sm:text-base text-gray-700 font-medium">Establecer como dirección predeterminada</span>
                    </label>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleSaveAddress}
                      className="flex-1 px-6 py-3.5 sm:py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl sm:rounded-2xl hover:from-primary-700 hover:to-primary-800 font-bold text-sm sm:text-base shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95"
                    >
                      {editingAddress ? 'Actualizar' : 'Guardar'} Dirección
                    </button>
                    <button
                      onClick={() => {
                        setShowAddressForm(false)
                        setEditingAddress(null)
                      }}
                      className="px-6 py-3.5 sm:py-4 border-2 border-gray-300 rounded-xl sm:rounded-2xl hover:bg-gray-50 font-semibold text-sm sm:text-base transition-all duration-200 active:scale-95"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {addresses.length === 0 ? (
                    <div className="text-center py-8 sm:py-12 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl sm:rounded-2xl">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-gradient-to-br from-amber-200 to-orange-200 rounded-full flex items-center justify-center">
                        <MapPin className="w-8 h-8 sm:w-10 sm:h-10 text-amber-700" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                        Agregar Ubicación para Completar Pedido
                      </h3>
                      <p className="text-gray-700 font-medium text-sm sm:text-base mb-6 max-w-md mx-auto">
                        Necesitas agregar una dirección de entrega para poder completar tu pedido. Haz clic en el botón de arriba para agregar tu ubicación.
                      </p>
                      <button
                        onClick={() => {
                          setEditingAddress(null)
                          setAddressForm({
                            name: '',
                            phone: user?.phone || '',
                            street: '',
                            city: '',
                            state: '',
                            zipCode: '',
                            instructions: '',
                            isDefault: false
                          })
                          setShowAddressForm(true)
                        }}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl sm:rounded-2xl hover:from-primary-700 hover:to-primary-800 font-bold text-sm sm:text-base shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95"
                      >
                        <Plus className="w-5 h-5" />
                        Agregar Ubicación
                      </button>
                    </div>
                  ) : (
                    addresses.map((address) => (
                      <div
                        key={address.id}
                        className={`p-4 sm:p-5 border-2 rounded-xl sm:rounded-2xl cursor-pointer transition-all duration-300 active:scale-[0.98] ${
                          selectedAddressId === address.id
                            ? 'border-primary-500 bg-gradient-to-br from-primary-50 to-primary-100 shadow-md'
                            : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-sm'
                        }`}
                        onClick={() => setSelectedAddressId(address.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {address.isDefault && (
                              <span className="inline-block mb-2 sm:mb-3 px-3 py-1.5 text-xs sm:text-sm bg-primary-600 text-white rounded-full font-semibold">
                                Predeterminada
                              </span>
                            )}
                            <p className="font-bold text-gray-900 text-base sm:text-lg mb-1">{address.street}</p>
                            <p className="text-sm sm:text-base text-gray-600">
                              {address.city}, {address.state} {address.zipCode}
                            </p>
                            {address.instructions && (
                              <p className="text-sm text-gray-500 mt-2">{address.instructions}</p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditAddress(address)
                              }}
                              className="p-2 sm:p-2.5 text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-all duration-200 active:scale-95"
                            >
                              <Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteAddress(address.id)
                              }}
                              className="p-2 sm:p-2.5 text-red-600 hover:bg-red-50 active:bg-red-100 rounded-xl transition-all duration-200 active:scale-95"
                            >
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>
                        </div>
                        {selectedAddressId === address.id && (
                          <div className="mt-3 sm:mt-4 flex items-center gap-2 text-primary-600 font-semibold">
                            <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                            <span className="text-sm sm:text-base">Seleccionada</span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-6 lg:p-7">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-5 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl sm:rounded-2xl flex items-center justify-center">
                  <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                Método de Pago
              </h2>
              <div className="space-y-3 sm:space-y-4">
                <label className={`flex items-center gap-4 p-4 sm:p-5 border-2 rounded-xl sm:rounded-2xl cursor-pointer transition-all duration-300 active:scale-[0.98] ${
                  paymentMethod === 'cash'
                    ? 'border-primary-500 bg-gradient-to-br from-primary-50 to-primary-100 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-sm'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="cash"
                    checked={paymentMethod === 'cash'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 focus:ring-primary-500 focus:ring-2"
                  />
                  <div className="flex-1">
                    <span className="font-bold text-gray-900 text-base sm:text-lg block mb-1">Efectivo</span>
                    <p className="text-sm sm:text-base text-gray-600">Pago al momento de la entrega</p>
                  </div>
                  {paymentMethod === 'cash' && (
                    <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 text-primary-600" />
                  )}
                </label>
                <label className={`flex items-center gap-4 p-4 sm:p-5 border-2 rounded-xl sm:rounded-2xl cursor-pointer transition-all duration-300 active:scale-[0.98] ${
                  paymentMethod === 'card'
                    ? 'border-primary-500 bg-gradient-to-br from-primary-50 to-primary-100 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-sm'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 focus:ring-primary-500 focus:ring-2"
                  />
                  <div className="flex-1">
                    <span className="font-bold text-gray-900 text-base sm:text-lg block mb-1">Tarjeta</span>
                    <p className="text-sm sm:text-base text-gray-600">Pago con tarjeta de crédito/débito</p>
                  </div>
                  {paymentMethod === 'card' && (
                    <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 text-primary-600" />
                  )}
                </label>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-6 lg:p-7">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-5">Notas Adicionales</h2>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white resize-none"
                placeholder="Instrucciones especiales para tu pedido..."
              />
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-7 sticky top-4">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-5 sm:mb-6">Resumen del Pedido</h2>

              <div className="space-y-3 sm:space-y-4 mb-5 sm:mb-6 max-h-[300px] overflow-y-auto">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 sm:gap-4 pb-3 sm:pb-4 border-b border-gray-100 last:border-0">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl overflow-hidden shadow-sm">
                      <ProductImage
                        imageKey={item.product.imageUrl || null}
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 text-sm sm:text-base line-clamp-1">{item.product.name}</p>
                      <p className="text-xs sm:text-sm text-gray-600">Cantidad: {item.quantity}</p>
                      <p className="text-sm sm:text-base font-bold text-primary-600 mt-1">RD${(Number(item.total) || 0).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t-2 border-gray-200 pt-4 sm:pt-5 space-y-2 sm:space-y-3">
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold text-gray-900">RD${(Number(subtotal) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-600">Entrega</span>
                  <span className="font-semibold text-gray-900">RD${(Number(deliveryFee) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-600">ITBIS (18%)</span>
                  <span className="font-semibold text-gray-900">RD${(Number(taxAmount) || 0).toFixed(2)}</span>
                </div>
                <div className="border-t-2 border-gray-200 pt-4 sm:pt-5 flex justify-between">
                  <span className="font-bold text-gray-900 text-base sm:text-lg">Total</span>
                  <span className="font-bold text-primary-600 text-xl sm:text-2xl lg:text-3xl">RD${(Number(finalTotal) || 0).toFixed(2)}</span>
                </div>
              </div>

              {addresses.length === 0 ? (
                <div className="mt-6 sm:mt-8 p-4 sm:p-5 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl sm:rounded-2xl">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-6 h-6 sm:w-7 sm:h-7 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-sm sm:text-base mb-1">
                        Agregar Ubicación Requerida
                      </h3>
                      <p className="text-gray-700 text-xs sm:text-sm mb-3">
                        Debes agregar una dirección de entrega para completar tu pedido.
                      </p>
                      <button
                        onClick={() => {
                          setEditingAddress(null)
                          setAddressForm({
                            name: '',
                            phone: user?.phone || '',
                            street: '',
                            city: '',
                            state: '',
                            zipCode: '',
                            instructions: '',
                            isDefault: false
                          })
                          setShowAddressForm(true)
                        }}
                        className="w-full px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 font-semibold text-sm transition-all duration-300 active:scale-95"
                      >
                        <Plus className="w-4 h-4 inline mr-2" />
                        Agregar Ubicación
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
              <button
                onClick={handleSubmitOrder}
                disabled={!selectedAddressId || submitting}
                className={`w-full mt-6 sm:mt-8 py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 active:scale-95 ${
                  selectedAddressId && !submitting
                    ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                  {submitting ? 'Procesando...' : !selectedAddressId ? 'Selecciona una Dirección' : 'Confirmar Pedido'}
              </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Checkout
